import { useState, useRef } from 'react';
import { useBusinessUnit } from '@/App';
import { dataImport } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Upload, FileSpreadsheet, ArrowRight, Check, AlertCircle, Home, Briefcase, X, Loader2, CheckCircle2, Download, FileText, ArrowRightLeft } from 'lucide-react';

// Excel template columns for each business unit
const MORTGAGE_TEMPLATE_COLUMNS = [
  'DLC Statement No', 'Statement Date', 'Agent Name', 'Co-Agent Name', 'Lender', 'Product',
  'Velocity Ref', 'Description', 'Mortgage Loan', 'FF %', 'VB %', 'Bonus %',
  'Agent Split %', 'Co-Agent Split %', 'Co-Brokering %', 'Memo', 'Payment Date'
];

const INSURANCE_TEMPLATE_COLUMNS = [
  'Statement No', 'Statement Date', 'Agent Name', 'Carrier', 'Product', 'Policy Number',
  'Description', 'Premium Amount', 'Commission Rate', 'Agent Split %', 'Co-Agent Split %',
  'Payment Date'
];

export default function Import() {
  const { businessUnit } = useBusinessUnit();
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  
  const buLabel = businessUnit === 'insurance' ? 'Insurance & Investment' : 'Mortgage';
  const BuIcon = businessUnit === 'insurance' ? Briefcase : Home;
  
  // State
  const [step, setStep] = useState(1); // 1: Upload, 2: Map, 3: Review, 4: Complete
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [mappings, setMappings] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [pdfConverting, setPdfConverting] = useState(false);

  // Download Excel template
  const downloadTemplate = () => {
    const columns = businessUnit === 'insurance' ? INSURANCE_TEMPLATE_COLUMNS : MORTGAGE_TEMPLATE_COLUMNS;
    
    // Create CSV content (Excel can open CSV files)
    const csvContent = columns.join(',') + '\n' + 
      // Add sample row with placeholders
      columns.map((col, idx) => {
        if (col.includes('Date')) return '2026-01-15';
        if (col.includes('%')) return '85';
        if (col.includes('Loan') || col.includes('Amount') || col.includes('Premium')) return '500000';
        if (col.includes('Rate')) return '0.05';
        if (col.includes('No') || col.includes('Ref')) return 'SAMPLE-001';
        if (col.includes('Name')) return 'Sample Name';
        return 'Sample Value';
      }).join(',');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${businessUnit}_commission_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded! Open with Excel to edit.');
  };

  // Handle PDF to Excel conversion
  const handlePdfConvert = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    
    setPdfConverting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/convert/pdf-to-excel?business_unit=${businessUnit}`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Conversion failed');
      }
      
      const data = await response.json();
      
      // Set preview data and proceed to mapping
      setPreviewData({
        headers: data.headers,
        preview_data: data.preview_data,
        system_fields: data.system_fields,
        auto_mappings: data.auto_mappings,
        source: 'pdf',
        filename: data.filename,
        total_rows: data.total_rows
      });
      setMappings(data.auto_mappings || {});
      setStep(2);
      
      toast.success(`PDF "${file.name}" converted successfully! ${data.total_rows} rows extracted. Please review column mappings.`);
    } catch (err) {
      toast.error(err.message || 'Failed to convert PDF');
    } finally {
      setPdfConverting(false);
      if (pdfInputRef.current) {
        pdfInputRef.current.value = '';
      }
    }
  };

  // Handle PDF download only (without mapping)
  const handlePdfDownloadOnly = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    
    setPdfConverting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/convert/pdf-to-excel/download`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Conversion failed');
      }
      
      // Download the converted file
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name.replace('.pdf', '_converted.xlsx');
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('PDF converted to Excel successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to convert PDF');
    } finally {
      setPdfConverting(false);
    }
  };
  
  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setLoading(true);
    try {
      const response = await dataImport.preview(file);
      setPreviewData(response.data);
      setMappings(response.data.auto_mappings || {});
      setStep(2);
      toast.success(`File "${file.name}" loaded successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to read file');
    } finally {
      setLoading(false);
    }
  };
  
  // Update mapping
  const updateMapping = (systemField, fileColumn) => {
    setMappings(prev => ({
      ...prev,
      [systemField]: fileColumn === '_none_' ? undefined : fileColumn
    }));
  };
  
  // Execute import
  const executeImport = async () => {
    setLoading(true);
    try {
      // Fetch full data if needed (for now using preview data)
      const response = await dataImport.execute(previewData.preview_data, mappings);
      setImportResult(response.data);
      setStep(4);
      toast.success(response.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };
  
  // Reset and start over
  const resetImport = () => {
    setStep(1);
    setPreviewData(null);
    setMappings({});
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  return (
    <div className="space-y-6 animate-fadeIn" data-testid="import-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${businessUnit === 'insurance' ? 'bg-blue-100' : 'bg-emerald-100'}`}>
            <BuIcon className={`w-5 h-5 ${businessUnit === 'insurance' ? 'text-blue-600' : 'text-emerald-600'}`} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Import Data
            </h1>
            <p className="text-slate-500">{buLabel} Division - Import commission data from Excel or CSV files</p>
          </div>
        </div>
      </div>
      
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-2">
        {[
          { num: 1, label: 'Upload File' },
          { num: 2, label: 'Map Columns' },
          { num: 3, label: 'Review' },
          { num: 4, label: 'Complete' }
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              step === s.num 
                ? 'bg-emerald-100 text-emerald-700' 
                : step > s.num 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-slate-100 text-slate-500'
            }`}>
              {step > s.num ? (
                <Check className="w-4 h-4" />
              ) : (
                <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-xs font-bold">
                  {s.num}
                </span>
              )}
              <span className="text-sm font-medium">{s.label}</span>
            </div>
            {idx < 3 && <ArrowRight className="w-4 h-4 mx-2 text-slate-300" />}
          </div>
        ))}
      </div>
      
      {/* Step 1: Upload File */}
      {step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Import Commission Data */}
          <Card className="border-slate-100 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Import Commission Data
              </CardTitle>
              <CardDescription>
                Upload an Excel (.xlsx) or CSV file containing your commission data
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Download Template Button */}
              <div className="flex justify-end mb-4">
                <Button variant="outline" onClick={downloadTemplate} className="gap-2">
                  <Download className="w-4 h-4" />
                  Download Template
                </Button>
              </div>
              
              <div 
                className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {loading ? (
                  <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-12 h-12 text-emerald-500 animate-spin" />
                    <p className="text-slate-600">Reading file...</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                    <p className="text-lg font-medium text-slate-700 mb-2">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-sm text-slate-500">
                      Excel (.xlsx, .xls) or CSV files supported
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* PDF to Excel Converter */}
          <Card className="border-slate-100 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5" />
                PDF to Excel Converter
              </CardTitle>
              <CardDescription>
                Convert PDF files and proceed directly to column mapping
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-700">
                  <strong>How it works:</strong> Upload a PDF file and we'll extract tables, then take you directly to the column mapping step for import.
                </p>
              </div>
              
              <div 
                className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-colors cursor-pointer"
                onClick={() => pdfInputRef.current?.click()}
              >
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfConvert}
                  className="hidden"
                  data-testid="pdf-upload-input"
                />
                {pdfConverting ? (
                  <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
                    <p className="text-slate-600">Converting PDF and extracting data...</p>
                  </div>
                ) : (
                  <>
                    <FileText className="w-12 h-12 mx-auto text-blue-400 mb-4" />
                    <p className="text-lg font-medium text-slate-700 mb-2">
                      Click to upload PDF
                    </p>
                    <p className="text-sm text-slate-500">
                      Converts PDF → Extracts data → Proceeds to mapping
                    </p>
                  </>
                )}
              </div>
              
              <div className="mt-4 text-xs text-slate-500">
                <p><strong>Note:</strong> Works best with PDFs containing tables. Image-based or scanned PDFs may not convert properly.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Step 2: Map Columns */}
      {step === 2 && previewData && (
        <div className="space-y-6">
          <Card className="border-slate-100 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5" />
                    Map Columns
                    {previewData.source === 'pdf' && (
                      <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        From PDF: {previewData.filename}
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Match your file columns to system fields
                    {previewData.total_rows && (
                      <span className="ml-2 text-emerald-600">({previewData.total_rows} rows extracted)</span>
                    )}
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={resetImport}>
                  <X className="w-4 h-4 mr-2" /> Start Over
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {previewData.system_fields.map(field => (
                  <div key={field.field} className="flex items-center gap-4">
                    <Label className="w-40 text-right text-sm">
                      {field.label}
                    </Label>
                    <Select
                      value={mappings[field.field] || '_none_'}
                      onValueChange={(val) => updateMapping(field.field, val)}
                    >
                      <SelectTrigger className="flex-1" data-testid={`mapping-${field.field}`}>
                        <SelectValue placeholder="Select column" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none_">-- Not Mapped --</SelectItem>
                        {previewData.headers.map(header => (
                          <SelectItem key={header} value={header}>{header}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {mappings[field.field] && (
                      <Check className="w-5 h-5 text-emerald-500" />
                    )}
                  </div>
                ))}
              </div>
              
              <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
                <Button variant="outline" onClick={resetImport}>
                  Cancel
                </Button>
                <Button onClick={() => setStep(3)} className="bg-emerald-600 hover:bg-emerald-700">
                  Continue to Review
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Step 3: Review & Import */}
      {step === 3 && previewData && (
        <div className="space-y-6">
          <Card className="border-slate-100 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Review & Import</CardTitle>
                  <CardDescription>
                    Preview your data before importing. Showing first {previewData.preview_data.length} rows.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setStep(2)}>
                    Back to Mapping
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Mapping Summary */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-slate-700 mb-3">Column Mappings:</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
                  {previewData.system_fields.filter(f => mappings[f.field]).map(field => (
                    <div key={field.field} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span className="text-slate-600">{field.label}:</span>
                      <span className="font-medium text-slate-900">{mappings[field.field]}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Data Preview Table */}
              <div className="border rounded-lg overflow-x-auto max-h-[400px]">
                <Table>
                  <TableHeader className="bg-slate-50 sticky top-0">
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      {previewData.system_fields.filter(f => mappings[f.field]).map(field => (
                        <TableHead key={field.field}>{field.label}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.preview_data.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-sm text-slate-500">{idx + 1}</TableCell>
                        {previewData.system_fields.filter(f => mappings[f.field]).map(field => (
                          <TableCell key={field.field} className="max-w-[200px] truncate">
                            {row[mappings[field.field]] || '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
                <Button variant="outline" onClick={resetImport}>
                  Cancel
                </Button>
                <Button 
                  onClick={executeImport} 
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import {previewData.preview_data.length} Records
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Step 4: Complete */}
      {step === 4 && importResult && (
        <Card className="border-slate-100 shadow-sm">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Import Complete!</h2>
              <p className="text-slate-600 mb-6">{importResult.message}</p>
              
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-6">
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-3xl font-bold text-emerald-600">{importResult.imported}</p>
                  <p className="text-sm text-emerald-700">Imported</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-3xl font-bold text-amber-600">{importResult.skipped}</p>
                  <p className="text-sm text-amber-700">Skipped</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-3xl font-bold text-blue-600">{importResult.created_agents?.length || 0}</p>
                  <p className="text-sm text-blue-700">New Agents</p>
                </div>
              </div>
              
              {importResult.created_agents?.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left max-w-md mx-auto">
                  <h4 className="font-medium text-blue-800 mb-2">New agents created:</h4>
                  <ul className="text-sm text-blue-700">
                    {importResult.created_agents.map((name, idx) => (
                      <li key={idx}>• {name}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {importResult.errors?.length > 0 && (
                <div className="bg-red-50 rounded-lg p-4 mb-6 text-left max-w-md mx-auto">
                  <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Errors ({importResult.errors.length}):
                  </h4>
                  <ul className="text-sm text-red-700">
                    {importResult.errors.map((err, idx) => (
                      <li key={idx}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <Button onClick={resetImport} className="bg-slate-900 hover:bg-slate-800">
                Import More Data
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
